import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Loader2, RefreshCw, AlertTriangle, Eye } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { fmtMoney, fmtNum } from "@/utils/currency";
import { PaymentModal } from "@/components/admin/PaymentModal";
import { StaffCompensation } from "@/components/admin/StaffCompensation";
import { PayoutApprovals } from "@/components/admin/PayoutApprovals";

const KIND_CHIP = {
  staff_compensation: { label: "Staff pay", cls: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300" },
  application_test: { label: "Hiring test", cls: "border-violet-500/30 bg-violet-500/10 text-violet-300" },
};
const KindChip = ({ kind }) =>
  KIND_CHIP[kind] ? (
    <span data-testid={`kind-chip-${kind}`} className={`ml-2 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${KIND_CHIP[kind].cls}`}>
      {KIND_CHIP[kind].label}
    </span>
  ) : null;

export const AdminPayments = () => {
  const [payments, setPayments] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mismatches, setMismatches] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/payments`, { headers: authHeaders() })
      .then((r) => setPayments(r.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load payments.");
      });
    axios
      .get(`${API}/admin/payments/reconcile-bonus`, { headers: authHeaders() })
      .then((r) => setMismatches(r.data.mismatches || []))
      .catch(() => {});
  }, [navigate]);

  useEffect(load, [load]);

  const applyReconcile = async (row) => {
    setBusyId(row.payment_id);
    try {
      await axios.post(`${API}/admin/payments/${row.payment_id}/apply-bonus`, { bonus: row.video_bonus }, { headers: authHeaders() });
      toast.success(`Bonus corrected to ${fmtMoney(row.video_bonus)} for ${row.worker_name || "worker"}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not apply.");
    } finally { setBusyId(null); }
  };

  const { owed, paid, totalOwed, totalPaid } = useMemo(() => {
    const list = payments || [];
    const o = list.filter((p) => p.status === "owed");
    const pd = list.filter((p) => p.status === "paid");
    return {
      owed: o,
      paid: pd,
      totalOwed: o.reduce((s, p) => s + p.amount, 0),
      totalPaid: pd.reduce((s, p) => s + p.amount, 0),
    };
  }, [payments]);

  return (
    <div data-testid="admin-payments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Payments</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Send money yourself, then mark it paid here so the books stay correct.</p>
        </div>
        <button
          data-testid="payments-refresh-btn"
          onClick={load}
          className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)]"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div data-testid="payments-owed-total" className="rounded-3xl border border-amber-500/20 bg-amber-500/10 p-6">
          <p className="font-display text-3xl font-bold tracking-tight text-amber-300">{fmtMoney(totalOwed)}</p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-400/70">Currently owed</p>
        </div>
        <div data-testid="payments-paid-total" className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-6">
          <p className="font-display text-3xl font-bold tracking-tight text-emerald-300">{fmtMoney(totalPaid)}</p>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-400/70">Total paid out</p>
        </div>
      </div>

      {mismatches.length > 0 && (
        <div data-testid="bonus-reconcile-banner" className="mt-6 rounded-3xl border border-amber-500/30 bg-amber-500/10 p-5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <p className="text-sm font-bold text-amber-300">Bonus mismatch review — {mismatches.length} payment{mismatches.length > 1 ? "s" : ""}</p>
          </div>
          <p className="mt-1 text-xs text-amber-200/70">These were edited from the Video tab before bonuses synced with payments. Nothing is changed until you confirm each one.</p>
          <div className="mt-3 space-y-2">
            {mismatches.map((m) => (
              <div key={m.payment_id} data-testid={`reconcile-row-${m.payment_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-black/20 px-4 py-2.5">
                <p className="text-xs font-semibold text-amber-100">
                  {m.worker_name || "Worker"} — {m.job_title || "Job"}
                  <span className="ml-2 text-amber-300/70">payment bonus {fmtMoney(m.payment_bonus)} · video says {fmtMoney(m.video_bonus)} · {m.status}</span>
                </p>
                <button data-testid={`reconcile-apply-${m.payment_id}`} onClick={() => applyReconcile(m)} disabled={busyId === m.payment_id} className="rounded-full bg-amber-500 px-3.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-amber-600 disabled:opacity-50">
                  {busyId === m.payment_id ? <Loader2 className="h-3 w-3 animate-spin" /> : `Set bonus to ${fmtMoney(m.video_bonus)}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!payments ? (
        <p data-testid="payments-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading payments...</p>
      ) : (
        <>
          <h2 className="mt-8 font-display text-base font-bold text-[var(--pv-text)]">Owed — pay these next</h2>
          <div className="mt-4 space-y-3">
            {owed.length === 0 ? (
              <div data-testid="owed-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-12">
                <Wallet className="h-8 w-8 text-[var(--pv-text-2)]" />
                <p className="text-sm font-medium text-[var(--pv-text-4)]">Nothing owed. All caught up.</p>
              </div>
            ) : (
              owed.map((p) => (
                <div key={p.id} data-testid={`owed-card-${p.id}`} onClick={() => setSelected(p)} className="flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5 transition-colors hover:border-cyan-500/30">
                  <div>
                    <p className="font-display text-base font-bold text-[var(--pv-text)]">{p.worker_name || "Unknown worker"}</p>
                    <p className="mt-0.5 text-xs font-medium text-[var(--pv-text-4)]">
                      {p.job_title || "Job"}
                      <KindChip kind={p.kind} />
                      <span className="mx-2 text-[var(--pv-text-2)]">|</span>
                      Approved {new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      {(p.bonus ?? 0) > 0 && <span className="ml-2 text-amber-400/80">base {fmtMoney(p.base)} + bonus {fmtMoney(p.bonus)}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-display text-xl font-bold text-amber-400">{fmtMoney(p.amount)}</span>
                    <button
                      data-testid={`mark-paid-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setSelected(p); }}
                      className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white  shadow-emerald-500/25 transition-colors hover:bg-emerald-600 disabled:opacity-60"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Review &amp; mark paid
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <h2 className="mt-10 font-display text-base font-bold text-[var(--pv-text)]">Payment history</h2>
          <div className="mt-4 space-y-3">
            {paid.length === 0 ? (
              <p data-testid="paid-empty" className="rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-10 text-center text-sm font-medium text-[var(--pv-text-4)]">No payments sent yet.</p>
            ) : (
              paid.map((p) => (
                <div key={p.id} data-testid={`paid-card-${p.id}`} onClick={() => setSelected(p)} className="flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5 transition-colors hover:border-cyan-500/30">
                  <div>
                    <p className="font-display text-base font-bold text-[var(--pv-text)]">{p.worker_name || "Unknown worker"}</p>
                    <p className="mt-0.5 text-xs font-medium text-[var(--pv-text-4)]">
                      {p.job_title || "Job"}
                      <KindChip kind={p.kind} />
                      {p.paid_at && (
                        <>
                          <span className="mx-2 text-[var(--pv-text-2)]">|</span>
                          Paid {new Date(p.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </>
                      )}
                      {p.method && <span className="ml-2 text-[var(--pv-text-3)]">via {p.method}</span>}
                      {(p.bonus ?? 0) > 0 && <span className="ml-2 text-emerald-400/70">base {fmtMoney(p.base)} + bonus {fmtMoney(p.bonus)}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.has_proof && (
                      <span data-testid={`proof-badge-${p.id}`} className="flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                        <Eye className="h-3 w-3" /> Proof
                      </span>
                    )}
                    <span className="font-display text-xl font-bold text-emerald-400">{fmtMoney(p.amount)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
      {selected && (
        <PaymentModal
          payment={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}

      <StaffCompensation />
      <PayoutApprovals />
    </div>
  );
};
